import { Entity, EventHandle, Quat, Vec3 } from 'playcanvas';

import { Events } from './events';
export type { QuatTuple, Vec3Tuple } from './math-types';

export type TransformTrackData = {
    /** Keyframe times, expressed in timeline frames (not seconds). Must be strictly increasing. */
    frames: number[];

    /** Flattened xyz positions, length = frames.length * 3. */
    positions: number[];

    /** Flattened xyzw quaternions, length = frames.length * 4. */
    rotations: number[];
};

export type VehicleMotionTrackSet = {
    body: TransformTrackData;
    wheels: [TransformTrackData, TransformTrackData, TransformTrackData, TransformTrackData];
};

export type VehicleMotionTargetSet = {
    body: Entity;
    wheels: [Entity, Entity, Entity, Entity];
};

const validateTransformTrack = (track: TransformTrackData): string | null => {
    if (!track || typeof track !== 'object') {
        return 'Track is missing.';
    }

    if (!Array.isArray(track.frames) || track.frames.length < 1) {
        return 'Track has no keyframes.';
    }

    if (!Array.isArray(track.positions)) {
        return 'Track positions missing.';
    }

    if (!Array.isArray(track.rotations)) {
        return 'Track rotations missing.';
    }

    if (track.positions.length !== track.frames.length * 3) {
        return `Track positions length mismatch (expected ${track.frames.length * 3}, got ${track.positions.length}).`;
    }

    if (track.rotations.length !== track.frames.length * 4) {
        return `Track rotations length mismatch (expected ${track.frames.length * 4}, got ${track.rotations.length}).`;
    }

    if (!Number.isFinite(track.frames[0])) {
        return 'Track frames contains non-finite values.';
    }

    for (let i = 1; i < track.frames.length; i++) {
        if (!Number.isFinite(track.frames[i])) {
            return 'Track frames contains non-finite values.';
        }

        if (!(track.frames[i] > track.frames[i - 1])) {
            return `Track frames must be strictly increasing (frames[${i}] <= frames[${i - 1}]).`;
        }
    }

    // Validate numeric payloads once up-front to prevent NaNs poisoning transforms.
    for (let i = 0; i < track.positions.length; i++) {
        if (!Number.isFinite(track.positions[i])) {
            return `Track positions contains non-finite values (positions[${i}]).`;
        }
    }

    for (let i = 0; i < track.rotations.length; i++) {
        if (!Number.isFinite(track.rotations[i])) {
            return `Track rotations contains non-finite values (rotations[${i}]).`;
        }
    }

    return null;
};

type Segment = { i0: number; i1: number; alpha: number };

const findSegment = (frames: number[], frame: number, out: Segment) => {
    const last = frames.length - 1;

    if (frame <= frames[0]) {
        out.i0 = 0;
        out.i1 = 0;
        out.alpha = 0;
        return out;
    }

    if (frame >= frames[last]) {
        out.i0 = last;
        out.i1 = last;
        out.alpha = 0;
        return out;
    }

    // Find i1: first index such that frames[i1] > frame.
    let lo = 0;
    let hi = last;
    while (lo + 1 < hi) {
        const mid = (lo + hi) >> 1;
        if (frames[mid] <= frame) {
            lo = mid;
        } else {
            hi = mid;
        }
    }

    const i0 = lo;
    const i1 = hi;
    const denom = frames[i1] - frames[i0];

    out.i0 = i0;
    out.i1 = i1;
    out.alpha = denom > 0 ? (frame - frames[i0]) / denom : 0;
    return out;
};

const evalTransformTrack = (
    track: TransformTrackData,
    frame: number,
    outPos: Vec3,
    outRot: Quat,
    scratchQ0: Quat,
    scratchQ1: Quat,
    scratchSeg: Segment
) => {
    const { i0, i1, alpha } = findSegment(track.frames, frame, scratchSeg);

    // Numerical safety: callers may pass fractional frame values.
    const t = alpha <= 0 ? 0 : (alpha >= 1 ? 1 : alpha);

    const p0 = i0 * 3;
    const p1 = i1 * 3;

    const x0 = track.positions[p0];
    const y0 = track.positions[p0 + 1];
    const z0 = track.positions[p0 + 2];

    if (i0 === i1 || t <= 0) {
        outPos.set(x0, y0, z0);
        outRot.set(
            track.rotations[i0 * 4],
            track.rotations[i0 * 4 + 1],
            track.rotations[i0 * 4 + 2],
            track.rotations[i0 * 4 + 3]
        );
        return;
    }

    if (t >= 1) {
        outPos.set(
            track.positions[p1],
            track.positions[p1 + 1],
            track.positions[p1 + 2]
        );
        outRot.set(
            track.rotations[i1 * 4],
            track.rotations[i1 * 4 + 1],
            track.rotations[i1 * 4 + 2],
            track.rotations[i1 * 4 + 3]
        );
        return;
    }

    const x1 = track.positions[p1];
    const y1 = track.positions[p1 + 1];
    const z1 = track.positions[p1 + 2];

    outPos.set(
        x0 + (x1 - x0) * t,
        y0 + (y1 - y0) * t,
        z0 + (z1 - z0) * t
    );

    scratchQ0.set(
        track.rotations[i0 * 4],
        track.rotations[i0 * 4 + 1],
        track.rotations[i0 * 4 + 2],
        track.rotations[i0 * 4 + 3]
    );

    scratchQ1.set(
        track.rotations[i1 * 4],
        track.rotations[i1 * 4 + 1],
        track.rotations[i1 * 4 + 2],
        track.rotations[i1 * 4 + 3]
    );

    outRot.slerp(scratchQ0, scratchQ1, t);
};

/**
 * Applies precomputed vehicle motion (local-space) transform tracks to a set of target entities.
 *
 * Notes:
 * - The input tracks are assumed to be in the local space of the parent hierarchy root.
 * - This controller clamps outside the keyframe range (no looping).
 */
class VehicleMotionController {
    private events: Events;
    private targets: VehicleMotionTargetSet;
    private tracks: VehicleMotionTrackSet;
    private enabled = true;

    private scratchPos = new Vec3();
    private scratchRot = new Quat();
    private scratchQ0 = new Quat();
    private scratchQ1 = new Quat();
    private scratchSeg: Segment = { i0: 0, i1: 0, alpha: 0 };

    private timeHandle: EventHandle | null = null;
    private frameHandle: EventHandle | null = null;
    private clearHandle: EventHandle | null = null;

    constructor(events: Events, targets: VehicleMotionTargetSet, tracks: VehicleMotionTrackSet) {
        this.events = events;
        this.targets = targets;
        this.tracks = tracks;

        const errors: string[] = [];
        const bodyErr = validateTransformTrack(tracks.body);
        if (bodyErr) errors.push(`Body: ${bodyErr}`);
        tracks.wheels.forEach((wheelTrack, i) => {
            const e = validateTransformTrack(wheelTrack);
            if (e) errors.push(`Wheel ${i + 1}: ${e}`);
        });

        if (errors.length > 0) {
            console.warn(`VehicleMotionController: invalid track set:\n${errors.map(e => ` - ${e}`).join('\n')}`);
            this.enabled = false;
            return;
        }

        // Evaluate on timeline playback and scrub.
        this.timeHandle = events.on('timeline.time', (time: number) => {
            this.evaluate(time);
        });

        this.frameHandle = events.on('timeline.frame', (frame: number) => {
            this.evaluate(frame);
        });

        // Clean up when the scene clears.
        this.clearHandle = events.on('scene.clear', () => {
            this.destroy();
        });

        // Apply immediately at the current timeline frame so newly created targets
        // appear in the correct pose even if no timeline event fires (e.g. importing
        // while already at frame 0 and not playing).
        const currentFrame = events.invoke('timeline.frame') as number;
        this.evaluate((typeof currentFrame === 'number' && Number.isFinite(currentFrame)) ? currentFrame : 0);
    }

    getTracks(): VehicleMotionTrackSet {
        return this.tracks;
    }

    setEnabled(value: boolean) {
        this.enabled = value;
    }

    destroy() {
        this.timeHandle?.off();
        this.frameHandle?.off();
        this.clearHandle?.off();
        this.timeHandle = null;
        this.frameHandle = null;
        this.clearHandle = null;
        this.enabled = false;
    }

    private evaluate(frame: number) {
        if (!this.enabled) return;

        // body
        evalTransformTrack(this.tracks.body, frame, this.scratchPos, this.scratchRot, this.scratchQ0, this.scratchQ1, this.scratchSeg);
        this.targets.body.setLocalPosition(this.scratchPos);
        this.targets.body.setLocalRotation(this.scratchRot);

        // wheels
        for (let i = 0; i < 4; i++) {
            evalTransformTrack(this.tracks.wheels[i], frame, this.scratchPos, this.scratchRot, this.scratchQ0, this.scratchQ1, this.scratchSeg);
            this.targets.wheels[i].setLocalPosition(this.scratchPos);
            this.targets.wheels[i].setLocalRotation(this.scratchRot);
        }
    }
}

export { VehicleMotionController };
